import SidebarLeftMode from "../viewer/settings/SidebarLeftMode";
import SidebarRightMode from "../viewer/settings/SidebarRightMode";
import PortalDocument from "./PortalDocument";
import PageSelection from "./PageSelection";
import {Attachment, InfoForm, Parameter} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import ViewerSettings from "./ViewerSettings";
import PortalDocumentManager from "./PortalDocumentManager";
import MouseFunction from "../viewer/settings/MouseFunction";
import Translate from "../../shared/translation/Translate";
import MouseMode from "../viewer/settings/MouseMode";
import {initSidebarFunctionGlobals} from "./components/pdf/SidebarFunctions";
import {initAreaLayerGlobals} from "../viewer/pdf/AreaLayerInterface";
import HighlighterLanguage from "../dialog/parameter/HighlighterLanguage";
import {initFormLayerGlobals} from "../viewer/pdf/form/FormLayerInterface";

export interface DocumentSettingsInterface {
	locked: boolean;
	closed: boolean;
	selected: boolean;
	editArchiveConfirmation: boolean;
	fullscreen: boolean;
	attachmentPreview: boolean;
	previewZoomValue: number;
	annotationSelection: string | null;
	attachmentSelection: Array<string>;
	sidebarLeftMode: SidebarLeftMode;
	sidebarRightMode: SidebarRightMode;
	pageSelection: PageSelection;
	portfolioSelection?: string;
	portfolioFolder?: string;
	searchQuery: string;
	textWrap: boolean;
	viewAsHTML: boolean;
	viewAsMarkdown: boolean;
	viewerSettings: ViewerSettings;
	fullscreenSettings: ViewerSettings;
	hightlighterLanguage: HighlighterLanguage;
	formInfo?: InfoForm;
	hideFormMessage: boolean;
}

export interface DocumentSettingsViewport {
	width: number;
	height: number;
}

export default class DocumentSettings implements DocumentSettingsInterface, Parameter {
	private _locked: boolean;
	private _closed: boolean;
	private _selected: boolean;
	private _editArchiveConfirmation: boolean;
	private _fullscreen: boolean;
	private _attachmentPreview: boolean;
	private _previewZoomValue: number;
	private _annotationSelection: string | null;
	private _attachmentSelection: Array<string>;
	private _sidebarLeftMode: SidebarLeftMode;
	private _sidebarRightMode: SidebarRightMode;
	private _pageSelection: PageSelection;
	private _portfolioSelection?: string;
	private _portfolioFolder?: string;
	private _searchQuery: string;
	private _textWrap: boolean;
	private _viewAsHTML: boolean;
	private _viewAsMarkdown: boolean;
	private _viewerSettings: ViewerSettings;
	private _fullscreenSettings: ViewerSettings;
	private _hightlighterLanguage: HighlighterLanguage;
	private _formInfo?: InfoForm;
	private _hideFormMessage: boolean;
	private _showSidebarFunction: boolean;

	/**
	 * Constructs a {@link DocumentSettings} instance
	 */
	constructor(data?: any) {
		this._locked = FunctionHelper.exists(data?.locked) ? data.locked : false;
		this._closed = FunctionHelper.exists(data?.closed) ? data.closed : true;
		this._selected = FunctionHelper.exists(data?.selected) ? data.selected : false;
		this._editArchiveConfirmation = FunctionHelper.exists(data?.editArchiveConfirmation) ?
			data.editArchiveConfirmation : false;
		this._fullscreen = FunctionHelper.exists(data?.fullscreen) ? data.fullscreen : false;
		this._attachmentPreview = FunctionHelper.exists(data?.attachmentPreview) ? data.attachmentPreview : true;
		this._previewZoomValue = FunctionHelper.exists(data?.previewZoomValue) ? data.previewZoomValue : 75;
		this._annotationSelection = FunctionHelper.exists(data?.annotationSelection) ? data.annotationSelection : null;
		this._attachmentSelection = FunctionHelper.exists(data?.attachmentSelection) ? data.attachmentSelection : [];
		this._sidebarLeftMode = FunctionHelper.exists(data?.sidebarLeftMode) ?
			data.sidebarLeftMode : SidebarLeftMode.NONE;
		this._sidebarRightMode = FunctionHelper.exists(data?.sidebarRightMode) ?
			data.sidebarRightMode : SidebarRightMode.NONE;
		this._pageSelection = FunctionHelper.exists(data?.pageSelection) ?
			PageSelection.fromJson(data.pageSelection) : new PageSelection();
		this._searchQuery = FunctionHelper.exists(data?.searchQuery) ? data.searchQuery : "";
		this._textWrap = FunctionHelper.exists(data?.textWrap) ? data.textWrap : false;
		this._viewAsHTML = FunctionHelper.exists(data?.viewAsHTML) ? data.viewAsHTML : true;
		this._viewAsMarkdown = FunctionHelper.exists(data?.viewAsMarkdown) ? data.viewAsMarkdown : false;
		this._viewerSettings = new ViewerSettings(data?.viewerSettings);
		this._fullscreenSettings = new ViewerSettings(data?.fullscreenSettings);
		this._portfolioSelection = data?.portfolioSelection;
		this._portfolioFolder = data?.portfolioFolder;
		this._hightlighterLanguage = HighlighterLanguage.AUTO;
		this._hideFormMessage = FunctionHelper.exists(data?.hideFormMessage) ? data.hideFormMessage : false;
		this._showSidebarFunction = FunctionHelper.exists(data?.showSidebarFunction) ? data.showSidebarFunction : false;
	}

	public static fromDocument(portalDocument?: PortalDocument): DocumentSettings {
		let result = new DocumentSettings();
		result.viewerSettings = ViewerSettings.fromDocument(portalDocument);
		result.fullscreenSettings = ViewerSettings.fromDocument(portalDocument);
		result.portfolioSelection = portalDocument?.metadata?.folders?.initialFile;

		if (FunctionHelper.exists(result.portfolioSelection)) {
			let initialFile: Attachment | undefined = portalDocument?.getAttachment(result.portfolioSelection!);

			if (FunctionHelper.exists(initialFile?.folderId)) {
				result.portfolioFolder = portalDocument?.getFolderById(initialFile!.folderId!)?.path;
			}
		}

		return result;
	}

	get locked(): boolean {
		return this._locked;
	}

	set locked(value: boolean) {
		this._locked = value;
	}

	get closed(): boolean {
		return this._closed;
	}

	set closed(value: boolean) {
		this._closed = value;
	}

	get selected(): boolean {
		return this._selected;
	}

	set selected(value: boolean) {
		this._selected = value;
	}

	get editArchiveConfirmation(): boolean {
		return this._editArchiveConfirmation;
	}

	set editArchiveConfirmation(value: boolean) {
		this._editArchiveConfirmation = value;
	}

	get fullscreen(): boolean {
		return this._fullscreen;
	}

	set fullscreen(value: boolean) {
		this._fullscreen = value;
	}

	get attachmentPreview(): boolean {
		return this._attachmentPreview;
	}

	set attachmentPreview(value: boolean) {
		this._attachmentPreview = value;
	}

	get previewZoomValue(): number {
		return this._previewZoomValue;
	}

	set previewZoomValue(value: number) {
		this._previewZoomValue = value;
	}

	get annotationSelection(): string | null {
		return this._annotationSelection;
	}

	set annotationSelection(value: string | null) {
		this._annotationSelection = value;
	}

	get attachmentSelection(): Array<string> {
		return this._attachmentSelection;
	}

	set attachmentSelection(value: Array<string>) {
		this._attachmentSelection = value;
	}

	get sidebarLeftMode(): SidebarLeftMode {
		return this._sidebarLeftMode;
	}

	set sidebarLeftMode(value: SidebarLeftMode) {
		this._sidebarLeftMode = value;
	}

	get sidebarRightMode(): SidebarRightMode {
		return this._sidebarRightMode;
	}

	set sidebarRightMode(value: SidebarRightMode) {
		this._sidebarRightMode = value;
	}

	get pageSelection(): PageSelection {
		return this._pageSelection;
	}

	set pageSelection(value: PageSelection) {
		this._pageSelection = value;
	}

	get portfolioSelection(): string | undefined {
		return this._portfolioSelection;
	}

	set portfolioSelection(value: string | undefined) {
		this._portfolioSelection = value;
	}

	get portfolioFolder(): string | undefined {
		return this._portfolioFolder;
	}

	set portfolioFolder(value: string | undefined) {
		this._portfolioFolder = value;
	}

	get searchQuery(): string {
		return this._searchQuery;
	}

	set searchQuery(value: string) {
		this._searchQuery = value;
	}

	get textWrap(): boolean {
		return this._textWrap;
	}

	set textWrap(value: boolean) {
		this._textWrap = value;
	}

	get viewAsHTML(): boolean {
		return this._viewAsHTML;
	}

	set viewAsHTML(value: boolean) {
		this._viewAsHTML = value;
	}

	get viewAsMarkdown(): boolean {
		return this._viewAsMarkdown;
	}

	set viewAsMarkdown(value: boolean) {
		this._viewAsMarkdown = value;
	}

	get activeViewer(): ViewerSettings {
		if (this.fullscreen) {
			return this.fullscreenSettings;
		}

		return this.viewerSettings;
	}

	get viewerSettings(): ViewerSettings {
		return this._viewerSettings;
	}

	set viewerSettings(value: ViewerSettings) {
		this._viewerSettings = value;
	}

	get fullscreenSettings(): ViewerSettings {
		return this._fullscreenSettings;
	}

	set fullscreenSettings(value: ViewerSettings) {
		this._fullscreenSettings = value;
	}

	get hightlighterLanguage(): HighlighterLanguage {
		return this._hightlighterLanguage;
	}

	set hightlighterLanguage(value: HighlighterLanguage) {
		this._hightlighterLanguage = value;
	}

	get formInfo(): InfoForm | undefined {
		return this._formInfo;
	}

	set formInfo(value: InfoForm | undefined) {
		this._formInfo = value;
	}

	get hideFormMessage(): boolean {
		return this._hideFormMessage;
	}

	set hideFormMessage(value: boolean) {
		this._hideFormMessage = value;
	}

	get showSidebarFunction(): boolean {
		return this._showSidebarFunction;
	}

	set showSidebarFunction(value: boolean) {
		this._showSidebarFunction = value;
	}

	public static fromJson(data: any): DocumentSettings {
		if (!FunctionHelper.exists(data)) {
			return data;
		}

		return new DocumentSettings(data);
	}

	public toJson(): any {
		return {
			'locked': this.locked,
			'closed': this.closed,
			'selected': this.selected,
			'editArchiveConfirmation': this.editArchiveConfirmation,
			'fullscreen': this.fullscreen,
			'attachmentPreview': this.attachmentPreview,
			'previewZoomValue': this.previewZoomValue,
			'annotationSelection': this.annotationSelection,
			'attachmentSelection': this.attachmentSelection,
			'sidebarLeftMode': this.sidebarLeftMode,
			'sidebarRightMode': this.sidebarRightMode,
			'pageSelection': this.pageSelection.toJson(),
			'portfolioSelection': this.portfolioSelection,
			'portfolioFolder': this.portfolioFolder,
			'searchQuery': this.searchQuery,
			'textWrap': this.textWrap,
			'viewAsHTML': this.viewAsHTML,
			'viewAsMarkdown': this.viewAsMarkdown,
			'viewerSettings': this.viewerSettings.toJson(),
			'fullscreenSettings': this.fullscreenSettings.toJson(),
			'hightlighterLanguage': this.hightlighterLanguage,
			'formInfo': this.formInfo?.toJson(),
			'hideFormMessage': this.hideFormMessage,
			'showSidebarFunction': this.showSidebarFunction
		};
	}

	public clone(): DocumentSettings {
		return DocumentSettings.fromJson(this.toJson());
	}

	public resetMouseMode(): void {
		window.getSelection()?.removeAllRanges();
		this.activeViewer.mouseMode = MouseMode.MOVE;
		this.activeViewer.mouseFunction = MouseFunction.NONE;
		this.activeViewer.mouseFunctionActive = false;
		this.showSidebarFunction = false;
	}

	public static checkActiveMouseFunction(props: any): boolean {
		let documentManager: PortalDocumentManager = props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		let activeFunction: boolean = false;

		for (let portalDocument of selection) {
			let documentSettings: DocumentSettings = portalDocument.getSettings();
			if (documentSettings.activeViewer.mouseFunctionActive) {
				activeFunction = true;
			}
		}

		return activeFunction;
	}

	public static async resetMouseFunction(props: any, callback?: () => Promise<void>): Promise<void> {
		let documentManager: PortalDocumentManager = props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			let documentSettings: DocumentSettings = portalDocument.getSettings();
			documentSettings.resetMouseMode();
		}

		documentManager.updateGlobalDocumentList();

		props.context.getPage().setState({
			...initAreaLayerGlobals(),
			...initFormLayerGlobals(),
			...initSidebarFunctionGlobals()
		});

		if (FunctionHelper.exists(callback)) {
			await callback!();
		}
	}

	public static confirmResetMouseFunction(props: any, callback?: () => Promise<void>): void {
		props.context.getPage().setState({
			showConfirmDialog: true,
			confirmDialogMessage: Translate.value("infos.progress.cancel", {ns: "message"}),
			confirmDialogSubmit: async () => {
				await DocumentSettings.resetMouseFunction(props, callback);
			}
		});
	}
}